import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Download, LogOut, RefreshCw, Mail, Plus, X, Save } from "lucide-react";
import api, { API } from "../lib/api";
import Monogram from "../components/Monogram";

const Stat = ({ label, value, testId }) => (
  <div data-testid={testId} className="border border-hairline bg-ivory p-6">
    <div className="text-[10px] tracking-[0.3em] uppercase text-gold">{label}</div>
    <div className="mt-3 font-serif text-4xl text-ink leading-none tracking-tight">{value}</div>
  </div>
);

const AdminDashboard = () => {
  const nav = useNavigate();
  const [rsvps, setRsvps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wedding, setWedding] = useState("traditional");
  const email = localStorage.getItem("am_admin_email");

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/admin/rsvps", { params: { wedding } }),
        api.get("/admin/stats", { params: { wedding } }),
      ]);
      setRsvps(r.data); setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("am_admin_token");
        nav("/admin/login");
      } else {
        toast.error("Failed to load RSVPs.");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!localStorage.getItem("am_admin_token")) { nav("/admin/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wedding]);

  const logout = () => {
    localStorage.removeItem("am_admin_token");
    localStorage.removeItem("am_admin_email");
    nav("/admin/login");
  };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("am_admin_token");
      const res = await fetch(`${API}/admin/rsvps/export.csv?wedding=${wedding}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `rsvps-${wedding}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch (e) { toast.error("Could not export CSV."); }
  };

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <Toaster richColors position="top-center" />
      <header className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <Monogram className="h-12 w-auto" />
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold">Admin</div>
          </div>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-ink tracking-tight">RSVP Overview</h1>
          <div className="mt-2 text-xs text-ink-faint">Signed in as {email}</div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} data-testid="admin-refresh" className="btn-gold inline-flex items-center gap-2 border border-gold text-gold text-[11px] tracking-[0.25em] uppercase px-5 py-3 rounded-full">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={downloadCsv} data-testid="admin-export-csv" className="btn-gold inline-flex items-center gap-2 border border-gold text-gold text-[11px] tracking-[0.25em] uppercase px-5 py-3 rounded-full">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 border border-hairline text-ink-soft text-[11px] tracking-[0.25em] uppercase px-5 py-3 rounded-full hover:border-burgundy hover:text-burgundy transition-colors">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </header>

      {/* Wedding tab switcher */}
      <div className="flex flex-wrap items-center gap-2 mb-10 border-b border-hairline" data-testid="wedding-tabs">
        {[
          { k: "traditional", label: "Traditional Wedding" },
          { k: "white", label: "White Wedding" },
          { k: "all", label: "All Combined" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setWedding(t.k)}
            data-testid={`tab-${t.k}`}
            className={`px-5 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors border-b-2 -mb-px ${
              wedding === t.k ? "border-gold text-ink" : "border-transparent text-ink-faint hover:text-ink-soft"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
            <Stat label="Total RSVPs" value={stats.total_submissions} testId="stat-total" />
            <Stat label="Attending" value={stats.total_attending} testId="stat-attending" />
            <Stat label="Not Attending" value={stats.total_not_attending} testId="stat-not-attending" />
            <Stat label="Plus-Ones" value={stats.total_plus_ones} testId="stat-plus-ones" />
            <Stat label="Total Headcount" value={stats.total_headcount} testId="stat-headcount" />
          </div>

          {wedding !== "traditional" && (
            <div className="border border-hairline bg-ivory p-6 mb-14" data-testid="dietary-breakdown">
              <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-4">Dietary Breakdown (Attending + Guests)</div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {Object.entries(stats.dietary_breakdown).map(([k, v]) => (
                  <div key={k} className="border-t border-hairline pt-3">
                    <div className="text-xs uppercase tracking-wider text-ink-faint">{k}</div>
                    <div className="font-serif text-2xl text-ink">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <NotificationEmailsPanel />

      <div className="border border-hairline bg-ivory overflow-hidden">
        <div className="px-6 py-4 border-b border-hairline text-[10px] tracking-[0.3em] uppercase text-gold">All Responses</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="rsvps-table">
            <thead className="text-[10px] tracking-[0.2em] uppercase text-ink-faint bg-cream/50">
              <tr>
                <th className="text-left p-4">Submitted</th>
                <th className="text-left p-4">Name</th>
                <th className="text-left p-4">Email</th>
                <th className="text-left p-4">Phone</th>
                <th className="text-left p-4">Attending</th>
                <th className="text-left p-4">Guest</th>
                {wedding !== "traditional" && <th className="text-left p-4">Dietary</th>}
                <th className="text-left p-4">Message</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={wedding !== "traditional" ? 8 : 7} className="p-10 text-center text-ink-faint">Loading…</td></tr>
              )}
              {!loading && rsvps.length === 0 && (
                <tr><td colSpan={wedding !== "traditional" ? 8 : 7} className="p-10 text-center text-ink-faint">No RSVPs yet.</td></tr>
              )}
              {rsvps.map((r) => (
                <tr key={r.id} className="border-t border-hairline hover:bg-cream/30 transition-colors" data-testid={`rsvp-row-${r.id}`}>
                  <td className="p-4 whitespace-nowrap text-ink-faint">{new Date(r.submitted_at).toLocaleString()}</td>
                  <td className="p-4 text-ink font-medium">{r.full_name}</td>
                  <td className="p-4 text-ink-soft">{r.email}</td>
                  <td className="p-4 text-ink-soft">{r.phone}</td>
                  <td className="p-4">
                    <span className={`inline-block px-2.5 py-1 text-[10px] tracking-widest uppercase border ${r.attending === "yes" ? "border-gold text-gold" : "border-burgundy text-burgundy"}`}>
                      {r.attending === "yes" ? "Attending" : "Regrets"}
                    </span>
                  </td>
                  <td className="p-4 text-ink-soft">
                    {r.bringing_guest === "yes" ? (
                      <>
                        <div className="text-ink">{r.guest_name}</div>
                        <div className="text-xs text-ink-faint">{r.guest_email}</div>
                      </>
                    ) : <span className="text-ink-faint">—</span>}
                  </td>
                  {wedding !== "traditional" && (
                    <td className="p-4 text-ink-soft">
                      {r.dietary}
                      {r.dietary_note && <div className="text-xs text-ink-faint">{r.dietary_note}</div>}
                    </td>
                  )}
                  <td className="p-4 text-ink-soft max-w-xs truncate" title={r.message || ""}>{r.message || <span className="text-ink-faint">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

// --- Notification Emails Panel --- //
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const EmailList = ({ label, items, onAdd, onRemove, testId }) => {
  const [draft, setDraft] = useState("");
  const add = (e) => {
    e.preventDefault();
    const v = draft.trim().toLowerCase();
    if (!EMAIL_RE.test(v)) { toast.error("Please enter a valid email."); return; }
    if (items.includes(v)) { toast.info("Already in the list."); return; }
    onAdd(v);
    setDraft("");
  };
  return (
    <div className="border border-hairline bg-ivory p-6" data-testid={testId}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-4">{label}</div>
      {items.length === 0 ? (
        <div className="text-sm text-ink-faint italic mb-4">No recipients set — falling back to the default couple emails.</div>
      ) : (
        <ul className="space-y-2 mb-4">
          {items.map((e) => (
            <li key={e} className="flex items-center justify-between gap-3 border-b border-hairline pb-2 text-sm">
              <span className="text-ink break-all">{e}</span>
              <button
                type="button"
                onClick={() => onRemove(e)}
                data-testid={`${testId}-remove-${e}`}
                className="text-ink-faint hover:text-burgundy transition-colors"
                aria-label={`Remove ${e}`}
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="flex items-center gap-2">
        <input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="name@example.com"
          data-testid={`${testId}-input`}
          className="flex-1 bg-transparent border-0 border-b border-hairline text-sm py-2 text-ink placeholder:text-ink-faint focus:outline-none focus:border-gold"
        />
        <button
          type="submit"
          data-testid={`${testId}-add`}
          className="btn-gold inline-flex items-center gap-1 border border-gold text-gold text-[10px] tracking-[0.25em] uppercase px-4 py-2 rounded-full"
        >
          <Plus size={12} /> Add
        </button>
      </form>
    </div>
  );
};

const NotificationEmailsPanel = () => {
  const [trad, setTrad] = useState([]);
  const [white, setWhite] = useState([]);
  const [fallback, setFallback] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings/notification-emails");
      setTrad(data.trad_emails || []);
      setWhite(data.white_emails || []);
      setFallback(data.fallback_emails || []);
      setDirty(false);
    } catch (e) {
      toast.error("Could not load notification emails.");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/notification-emails", {
        trad_emails: trad, white_emails: white,
      });
      toast.success("Notification recipients saved.");
      setDirty(false);
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally { setSaving(false); }
  };

  const mark = (setter) => (v) => { setter(v); setDirty(true); };

  return (
    <section className="mt-14 mb-14" data-testid="notification-emails-panel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-gold">
            <Mail size={12} /> RSVP Notifications
          </div>
          <h2 className="mt-2 font-serif text-3xl text-ink tracking-tight">Who gets notified</h2>
          <p className="mt-2 text-sm text-ink-soft max-w-2xl">
            When someone submits an RSVP, a copy of the confirmation is BCC&rsquo;d to the addresses below.
            Set separate lists for each wedding. If a list is empty, we&rsquo;ll fall back to the default couple emails
            {fallback.length ? ` (${fallback.join(", ")})` : ""}.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving || !dirty}
          data-testid="notification-emails-save"
          className="btn-gold inline-flex items-center gap-2 border border-gold text-gold text-[11px] tracking-[0.25em] uppercase px-5 py-3 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={14} /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <EmailList
          label="Traditional Wedding Recipients"
          items={trad}
          onAdd={(e) => mark(setTrad)([...trad, e])}
          onRemove={(e) => mark(setTrad)(trad.filter((x) => x !== e))}
          testId="trad-emails"
        />
        <EmailList
          label="White Wedding Recipients"
          items={white}
          onAdd={(e) => mark(setWhite)([...white, e])}
          onRemove={(e) => mark(setWhite)(white.filter((x) => x !== e))}
          testId="white-emails"
        />
      </div>
    </section>
  );
};
